import { API_BETA, jsonHeadersWithToken } from '@/lib/constants'
const BASE_URL = API_BETA

export const listWorkspaceMembers = async (workspaceId) => {
  if (!workspaceId) throw new Error('workspaceId is required')
  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}/members`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) throw new Error('Failed to fetch workspace members')
  return res.json()
}

export const inviteWorkspaceMember = async ({ workspaceId, email, permission }) => {
  if (!workspaceId || !email) throw new Error('workspaceId and email are required')
  const validPermissions = ['member', 'viewer', 'admin', 'guest']
  let normalizedPermission = (permission || 'member').toLowerCase()
  if (!validPermissions.includes(normalizedPermission)) {
    normalizedPermission = 'member'
  }

  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}/members/invite`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({ email, permission: normalizedPermission }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Failed to invite member')
  }
  return res.json()
}

export const updateWorkspaceMemberPermission = async ({ workspaceId, userId, permission }) => {
  if (!workspaceId || !userId) throw new Error('workspaceId and userId are required')
  const validPermissions = ['member', 'viewer', 'admin', 'guest']
  let normalizedPermission = (permission || 'member').toLowerCase()
  if (!validPermissions.includes(normalizedPermission)) {
    normalizedPermission = 'member'
  }

  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}/members/${userId}`, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({ permission: normalizedPermission }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Failed to update member permission')
  }
  return res.json()
}

export const removeWorkspaceMember = async ({ workspaceId, userId }) => {
  if (!workspaceId || !userId) throw new Error('workspaceId and userId are required')
  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}/members/${userId}`, {
    method: 'DELETE',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Failed to remove member')
  }
  return res.json()
}

export const cancelWorkspaceInvitation = async ({ workspaceId, invitationId }) => {
  if (!workspaceId || !invitationId) throw new Error('workspaceId and invitationId are required')
  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}/invitations/${invitationId}`, {
    method: 'DELETE',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Failed to cancel invitation')
  }
  return res.json()
}

export const listPendingInvitations = async () => {
  const res = await fetch(`${BASE_URL}workspaces/invitations/pending`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Failed to fetch pending invitations')
  }
  return res.json()
}

export const acceptInvitation = async (invitationId) => {
  if (!invitationId) throw new Error('invitationId is required')
  const res = await fetch(`${BASE_URL}workspaces/invitations/${invitationId}/accept`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Failed to accept invitation')
  }
  return res.json()
}

export const declineInvitation = async (invitationId) => {
  if (!invitationId) throw new Error('invitationId is required')
  const res = await fetch(`${BASE_URL}workspaces/invitations/${invitationId}/decline`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Failed to decline invitation')
  }
  return res.json()
}

export const resendInvitation = async (invitationId) => {
  if (!invitationId) throw new Error('invitationId is required')

  const url = `${BASE_URL}workspaces/invitations/${invitationId}/resend`
  console.log('[resendInvitation API] Calling:', url)

  const res = await fetch(url, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
  })

  console.log('[resendInvitation API] Response status:', res.status)
  const data = await res.json().catch(() => null)
  console.log('[resendInvitation API] Response data:', data)

  if (!res.ok) {
    const message = data?.errorMessage || data?.message || data?.error || 'Failed to resend invitation'
    throw new Error(message)
  }

  return data
}

/**
 * Transfer workspace ownership to another member
 * @param {string} workspaceId - The workspace ID
 * @param {string} newOwnerId - The user ID of the new owner
 */
export const transferWorkspaceOwnership = async ({ workspaceId, newOwnerId }) => {
  if (!workspaceId) throw new Error('workspaceId is required')
  if (!newOwnerId) throw new Error('newOwnerId is required')

  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}/transfer-ownership`, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({ newOwnerId }),
  })

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    const message = data?.errorMessage || data?.message || data?.error || 'Failed to transfer ownership'
    throw new Error(message)
  }

  return data
}
